import { apiClient, getErrorMessage } from '../../utils/apiClient';
import { logger } from '../../utils/logger';
import { toast } from '../../utils/toast';
import {
  API_ADMIN_UN_LOCODES_LIST, API_ADMIN_UN_LOCODES_CREATE,
  API_ADMIN_UN_LOCODES_GET, API_ADMIN_UN_LOCODES_UPDATE,
  API_ADMIN_UN_LOCODES_DELETE, API_ADMIN_UN_LOCODES_ACTIVATE,
  API_ADMIN_UN_LOCODES_DEACTIVATE, API_UN_LOCODES_ACTIVE,
} from '../../constants/apiConstants';

// ============================================================================
// Types
// ============================================================================

export interface UnLocode {
  id: number;
  countryCode: string;
  locationCode: string;
  locationName: string;
  subdivisionCode?: string;
  functionPort: boolean;
  functionAirport: boolean;
  functionRailTerminal: boolean;
  functionRoadTerminal: boolean;
  functionPostalExchange: boolean;
  functionMultimodal: boolean;
  functionBorderCrossing: boolean;
  iataCode?: string;
  latitude?: number;
  longitude?: number;
  sortOrder: number;
  isActive: boolean;
  status: string;
}

export interface CreateUnLocodeRequest {
  countryCode: string;
  locationCode: string;
  locationName: string;
  subdivisionCode?: string;
  functionPort?: boolean;
  functionAirport?: boolean;
  functionRailTerminal?: boolean;
  functionRoadTerminal?: boolean;
  functionPostalExchange?: boolean;
  functionMultimodal?: boolean;
  functionBorderCrossing?: boolean;
  iataCode?: string;
  latitude?: number;
  longitude?: number;
  sortOrder?: number;
}

export interface UpdateUnLocodeRequest {
  locationName: string;
  subdivisionCode?: string;
  functionPort?: boolean;
  functionAirport?: boolean;
  functionRailTerminal?: boolean;
  functionRoadTerminal?: boolean;
  functionPostalExchange?: boolean;
  functionMultimodal?: boolean;
  functionBorderCrossing?: boolean;
  iataCode?: string;
  latitude?: number;
  longitude?: number;
  sortOrder?: number;
}

// ============================================================================
// UN/LOCODE Service
// ============================================================================

export const unLocodeService = {
  /**
   * Fetches paginated UN/LOCODEs for admin management.
   */
  async getUnLocodes(searchText?: string, status?: string, pageNumber = 1, pageSize = 20, sortBy?: string, sortDirection?: string) {
    try {
      const response = await apiClient.get(API_ADMIN_UN_LOCODES_LIST, {
        params: { searchText, status, pageNumber, pageSize, sortBy, sortDirection },
      });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      const data = response.data.data;
      return { unLocodes: data?.locodes ?? [], totalCount: data?.totalCount ?? 0, pageNumber: data?.pageNumber ?? 1, pageSize: data?.pageSize ?? 20 };
    } catch (error) {
      logger.error('Failed to fetch UN/LOCODEs', error);
      throw error;
    }
  },

  /**
   * Fetches active UN/LOCODEs for dropdown consumption.
   */
  async getActiveUnLocodes(): Promise<UnLocode[]> {
    try {
      const response = await apiClient.get(API_UN_LOCODES_ACTIVE);
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data?.locodes ?? [];
    } catch (error) {
      logger.error('Failed to fetch active UN/LOCODEs', error);
      throw error;
    }
  },

  /**
   * Fetches a single UN/LOCODE by ID.
   */
  async getUnLocodeById(id: number): Promise<UnLocode> {
    try {
      const response = await apiClient.get(API_ADMIN_UN_LOCODES_GET(id));
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data?.locode;
    } catch (error) {
      logger.error('Failed to fetch UN/LOCODE', error);
      throw error;
    }
  },

  /**
   * Creates a new UN/LOCODE entry.
   */
  async createUnLocode(request: CreateUnLocodeRequest) {
    try {
      const response = await apiClient.post(API_ADMIN_UN_LOCODES_CREATE, request);
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('UN/LOCODE created successfully');
      return response.data.data;
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to create UN/LOCODE: ${msg}`);
      throw error;
    }
  },

  /**
   * Updates an existing UN/LOCODE entry.
   */
  async updateUnLocode(id: number, request: UpdateUnLocodeRequest) {
    try {
      const response = await apiClient.put(API_ADMIN_UN_LOCODES_UPDATE(id), request);
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('UN/LOCODE updated successfully');
      return response.data.data;
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to update UN/LOCODE: ${msg}`);
      throw error;
    }
  },

  /**
   * Soft-deletes a UN/LOCODE entry.
   */
  async deleteUnLocode(id: number) {
    try {
      const response = await apiClient.delete(API_ADMIN_UN_LOCODES_DELETE(id));
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('UN/LOCODE deleted successfully');
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to delete UN/LOCODE: ${msg}`);
      throw error;
    }
  },

  /**
   * Activates a UN/LOCODE entry.
   */
  async activateUnLocode(id: number) {
    try {
      const response = await apiClient.put(API_ADMIN_UN_LOCODES_ACTIVATE(id));
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('UN/LOCODE activated');
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to activate UN/LOCODE: ${msg}`);
      throw error;
    }
  },

  /**
   * Deactivates a UN/LOCODE entry.
   */
  async deactivateUnLocode(id: number) {
    try {
      const response = await apiClient.put(API_ADMIN_UN_LOCODES_DEACTIVATE(id));
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('UN/LOCODE deactivated');
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to deactivate UN/LOCODE: ${msg}`);
      throw error;
    }
  },
};
